import { useAuthContext } from "providers/Auth";
import { FEATURES } from "types/landlord/partner";

/**
 * Gates the underwriting agent (BSCPF-6141) in the portal: the Underwriting
 * agent card in Add AI Agent, the screening template's Underwriting agent
 * section and the agent's review on an application.
 *
 * Both keys arrive in the partner's `features` array:
 * - `ai_underwriting_agent` is the entitlement. Without it the API leaves
 *   underwriting agents out of the agents list and refuses to create, read or
 *   change one, so the UI would only show errors.
 * - `ui_underwriting_agent` is a `ui_` registry flag (Boom::UiFeatureFlag),
 *   toggled per partner from admin's /ui-feature-flags page. Granted to the
 *   partner's Boom staff only, it lets Boom run the agent before the partner's
 *   own users see it (BSCPF-6970). The API applies the same rule: a user the
 *   flag doesn't reach gets no underwriting agent in the agents list, none of
 *   its checks in the activity log, and a 403 from the agent's own routes.
 *
 * A partner that has not loaded yet counts as OFF.
 *
 * ── REMOVING THE UI FLAG ──────────────────────────────────────────────────
 * Drop `UI_UNDERWRITING_AGENT` from `FEATURES` and the check on it below, and
 * the flag check in boompay-api's `Boom::Partner#underwriting_agent_visible?`.
 */
export const useUnderwritingAgentsEnabled = () => {
  const { partner } = useAuthContext();

  return Boolean(
    partner?.features.includes(FEATURES.AI_UNDERWRITING_AGENT) &&
    partner.features.includes(FEATURES.UI_UNDERWRITING_AGENT)
  );
};

/**
 * Creating, changing or deleting an underwriting agent and its rules needs
 * screening-template access, the same check the API makes
 * (`authorize_screening_template_access!`).
 */
export const useCanManageUnderwritingAgents = () => {
  const { permissions } = useAuthContext();

  return (
    useUnderwritingAgentsEnabled() && permissions.canAccessScreeningTemplates
  );
};
